import ErrorMessage from "./ErrorMessage";
import PropTypes from "prop-types";
import React from 'react';
import Select from "react-select";
import classNames from 'classnames';
import { Field, useField, useFormikContext } from 'formik';
import { DatePickerField } from './DatePickerField';

const convertOptions = (options) => {
  return options.map(item => {
    if (typeof item === "string") {
      return { label: item, value: item };
    }
    return item;
  });
};

// Wraps react-select into a custom Formik component
// noinspection EqualityComparisonWithCoercionJS
const ReactSelect = ({ field, form, options, ...props }) => (
  <Select
    options={options}
    name={field.name}
    /* the "==" below is intentional as some fields use string options and others number */
    value={options ? options.find(option => option.value == field.value) : ''}
    onChange={option => form.setFieldValue(field.name, option ? option.value : '')}
    onBlur={field.onBlur}
    placeholder={null}
    {...props}
  />
);

// Renders Formik Field
const FieldHandler = ({ id, name, options, type, inputProps, helpers, ...extraProps }) => {
  const { setValue } = helpers;
  const { value: rawValue } = inputProps;

  // avoid null/undefined value to ensure component is controlled
  const value = rawValue === null || rawValue === undefined ? "" : rawValue;

  switch (type) {
    case "select":
      return (
        <Field
          id={id}
          name={name}
          options={options}
          component={ReactSelect}
          value={value}
          {...extraProps}
        />
      );

    case "checkbox":
    case "radio":
      return (
        <React.Fragment>
          {options.map((option, i) => {
            const optionClass = classNames("form-check", { "form-check-inline": type === "radio" });
            const props = {};

            // override default radio handling to support boolean values
            if (type === "radio") {
              props["checked"] = value === option.value;
              props["onChange"] = () => setValue(option.value);
            }

            return (
              <div key={`${i}-${option.value}-${option.label}`} className={optionClass}>
                <Field
                  type={type}
                  className="form-check-input"
                  id={`${id}--${option.value}`}
                  name={name}
                  value={option.value}
                  {...extraProps}
                  {...props}
                />
                <label className="form-check-label" htmlFor={`${id}--${option.value}`}>{option.label}</label>
              </div>
            );
          })}
        </React.Fragment>
      );

    case "date":
      return (
        <DatePickerField id={id} className="form-control" name={name} value={value} setValue={setValue} {...extraProps} />
      );

    default:
      return (
        <Field type={type} className="form-control" id={id} name={name} value={value} {...extraProps} />
      );
  }
};

/**
 * FormField encapsulates a formik input field, it's label, and validation error messaging for shared portal forms.
 * The form field renders as a column in a bootstrap grid layout.
 */
const FormField = (
  {
    colWidth,
    help,
    label,
    name,
    options,
    type,
    required,
    ...extraProps
  }
) => {
  const id = `form-field-${name}`;
  const colClassName = colWidth ? `col-${colWidth}` : "col";
  const [inputProps, _meta, helpers] = useField(name);
  const { errors } = useFormikContext();
  const className = classNames("sp-form-field", "mb-3", colClassName, { "sp-form-field-has-error": !!errors[name] }, { 'sp-required-field': required });

  return (
    <div className={className} data-sp-field={name}>
      <label className="form-label" htmlFor={id}>{label}</label>

      {help()}

      <div>
        <FieldHandler
          id={id}
          name={name}
          options={convertOptions(options)}
          type={type}
          inputProps={inputProps}
          helpers={helpers}
          {...extraProps}
        />
      </div>

      <ErrorMessage name={name} />
    </div>
  );
};

FormField.defaultProps = {
  options: [],
  help: () => (<div />),
  required: false
};

FormField.propTypes = {
  /** Field label */
  label: PropTypes.string.isRequired,

  /** Field name */
  name: PropTypes.string.isRequired,

  /** Field type */
  type: PropTypes.oneOf(["text", "date", "checkbox", "radio", "select"]).isRequired,

  /** Bootstrap grid column width */
  colWidth: PropTypes.number,

  /** Options for inputs with type "select" */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired
      })
    ])
  ),

  /** Function that renders optional help text */
  help: PropTypes.func,

  /** Whether to show the required icon */
  required: PropTypes.bool
};

export { ReactSelect };
export default FormField;
